import React, { useState, useEffect } from 'react';
import axios from '../utils/api';
import './ContainerCreationModal.css';
import { ContainerTemplate } from '../types/containerTemplates';
import { TemplateContainer } from '../types/template';

interface ContainerCreationModalProps {
  templateId: number; // ID HTML шаблона
  onCreateContainer: (container: TemplateContainer) => void;
  onClose: () => void;
}

type StringMap = Record<string, string>;

type AxiosErrorLike = {
  response?: {
    data?: {
      error?: string;
    };
  };
};

export function getApiErrorMessage(error: unknown, fallback: string): string {
  const maybe = error as AxiosErrorLike;
  return maybe?.response?.data?.error || fallback;
}

const ContainerCreationModal: React.FC<ContainerCreationModalProps> = ({
  templateId,
  onCreateContainer,
  onClose
}) => {
  const getAuthRequestConfig = () => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  // Состояния для данных
  const [containerTemplates, setContainerTemplates] = useState<ContainerTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<ContainerTemplate | null>(null);
  
  // Состояния формы
  const [containerName, setContainerName] = useState('');
  const [containerDescription, setContainerDescription] = useState('');
  const [customSettings, setCustomSettings] = useState<StringMap>({});
  const [customVisualStyle, setCustomVisualStyle] = useState<StringMap>({});
  
  // Состояния UI
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Загрузка шаблонов контейнеров при инициализации
  useEffect(() => {
    loadContainerTemplates();
    // Нам важно не менять поведение (загрузка при монтировании),
    // поэтому оставляем зависимости как раньше.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Обновление выбранного шаблона
  useEffect(() => {
    if (selectedTemplateId) {
      const template = containerTemplates.find(t => t.id === selectedTemplateId);
      setSelectedTemplate(template || null);
      
      // Устанавливаем имя по умолчанию на основе шаблона
      if (template && !containerName) {
        setContainerName(template.name);
      }
    } else {
      setSelectedTemplate(null);
    }
  }, [selectedTemplateId, containerTemplates, containerName]);

  const loadContainerTemplates = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get('/api/admin/container-templates?active=true', getAuthRequestConfig());
      
      setContainerTemplates(response.data);
    } catch (error) {
      console.error('Error loading container templates:', error);
      setError('Ошибка загрузки шаблонов контейнеров');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateContainer = async () => {
    if (!selectedTemplateId || !containerName.trim()) {
      setError('Выберите шаблон и введите название контейнера');
      return;
    }

    setCreating(true);
    setError(null);

    try {
      const response = await axios.post(
        '/api/admin/containers/create-from-template',
        {
          template_id: selectedTemplateId,
          html_template_id: templateId,
          name: containerName.trim(),
          description: containerDescription.trim() || undefined,
          custom_settings: Object.keys(customSettings).length > 0 ? customSettings : undefined,
          custom_visual_style: Object.keys(customVisualStyle).length > 0 ? customVisualStyle : undefined
        },
        getAuthRequestConfig()
      );

      console.log(`✅ Создан контейнер: ${containerName} из шаблона ${selectedTemplate?.name}`);
      onCreateContainer(response.data);
      onClose();
    } catch (error: unknown) {
      console.error('Error creating container:', error);
      setError(getApiErrorMessage(error, 'Ошибка создания контейнера'));
    } finally {
      setCreating(false);
    }
  };

  const handleCustomSettingChange = (key: string, value: string) => {
    if (value.trim()) {
      setCustomSettings((prev) => ({ ...prev, [key]: value }));
    } else {
      setCustomSettings((prev) => {
        const newSettings = { ...prev };
        delete newSettings[key];
        return newSettings;
      });
    }
  };

  const handleCustomStyleChange = (key: string, value: string) => {
    if (value.trim()) {
      setCustomVisualStyle((prev) => ({ ...prev, [key]: value }));
    } else {
      setCustomVisualStyle((prev) => {
        const newStyle = { ...prev };
        delete newStyle[key];
        return newStyle;
      });
    }
  };

  // Группировка шаблонов по типу
  const groupedTemplates = containerTemplates.reduce((groups, template) => {
    const type = template.container_type;
    if (!groups[type]) {
      groups[type] = [];
    }
    groups[type].push(template);
    return groups;
  }, {} as Record<string, ContainerTemplate[]>);

  const getTypeDisplayName = (type: string) => {
    switch (type) {
      case 'header': return 'Шапка сайта';
      case 'footer': return 'Подвал сайта';
      case 'menu': return 'Навигация';
      case 'dynamic': return 'Динамический контент';
      case 'custom': return 'Пользовательские';
      default: return type;
    }
  };

  const getPlacementTypeDisplayName = (type?: string) => {
    switch (type) {
      case 'any': return 'Любое размещение';
      case 'main-only': return 'Только в основной области';
      case 'sidebar-only': return 'Только в боковых колонках';
      case 'top': return 'Только сверху';
      case 'bottom': return 'Только снизу';
      default: return type || 'Не указано';
    }
  };

  return (
    <div className="container-creation-modal">
      <div className="modal-overlay" onClick={onClose}></div>
      
      <div className="modal-content">
        <div className="modal-header">
          <h2>Создание нового контейнера</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        {error && (
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            {error}
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}

        <div className="modal-body">
          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Загрузка шаблонов...</p>
            </div>
          ) : (
            <>
              <div className="form-section">
                <h3>Выберите тип контейнера</h3>
                <div className="template-groups">
                  {Object.entries(groupedTemplates).map(([type, templates]) => (
                    <div key={type} className="template-group">
                      <h4 className="group-title">{getTypeDisplayName(type)}</h4>
                      <div className="template-options">
                        {templates.map(template => (
                          <div 
                            key={template.id}
                            className={`template-option ${selectedTemplateId === template.id ? 'selected' : ''}`}
                            onClick={() => setSelectedTemplateId(template.id!)}
                          >
                            <div 
                              className="template-color" 
                              style={{ backgroundColor: template.color || '#007bff' }}
                            ></div>
                            <div className="template-info">
                              <div className="template-name">{template.name}</div>
                              <div className="template-description">
                                {template.description || 'Без описания'}
                              </div>
                              <div className="template-placement">
                                {getPlacementTypeDisplayName(template.placement_type)}
                              </div>
                            </div>
                            <div className="template-limits">
                              {template.min_components}-{template.max_components} комп.
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {selectedTemplate && (
                <div className="form-section">
                  <h3>Настройки контейнера</h3>
                  
                  <div className="form-group">
                    <label>Название контейнера: *</label>
                    <input
                      type="text"
                      value={containerName}
                      onChange={(e) => setContainerName(e.target.value)}
                      placeholder="Введите название контейнера"
                      maxLength={100}
                    />
                  </div>

                  <div className="form-group">
                    <label>Описание:</label>
                    <textarea
                      value={containerDescription}
                      onChange={(e) => setContainerDescription(e.target.value)}
                      placeholder="Описание назначения контейнера"
                      rows={3}
                      maxLength={500}
                    />
                  </div>

                  <div className="template-preview">
                    <h4>Параметры выбранного шаблона:</h4>
                    <div className="preview-grid">
                      <div className="preview-item">
                        <strong>Тип:</strong> {getTypeDisplayName(selectedTemplate.container_type)}
                      </div>
                      <div className="preview-item">
                        <strong>Размещение:</strong> {getPlacementTypeDisplayName(selectedTemplate.placement_type)}
                      </div>
                      <div className="preview-item">
                        <strong>Компонентов:</strong> от {selectedTemplate.min_components} до {selectedTemplate.max_components}
                      </div>
                      <div className="preview-item">
                        <strong>Иконка:</strong> {selectedTemplate.icon || 'default'}
                      </div>
                    </div>
                  </div>

                  <div className="advanced-settings">
                    <h4>Дополнительные настройки (необязательно)</h4>
                    
                    <div className="settings-row">
                      <div className="form-group">
                        <label>Отступы (padding):</label>
                        <input
                          type="text"
                          placeholder="10px 15px"
                          onChange={(e) => handleCustomStyleChange('padding', e.target.value)}
                        />
                      </div>
                      <div className="form-group">
                        <label>Отступы снаружи (margin):</label>
                        <input
                          type="text"
                          placeholder="5px 0"
                          onChange={(e) => handleCustomStyleChange('margin', e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="settings-row">
                      <div className="form-group">
                        <label>Граница (border):</label>
                        <input
                          type="text"
                          placeholder="1px solid #ccc"
                          onChange={(e) => handleCustomStyleChange('border', e.target.value)}
                        />
                      </div>
                      <div className="form-group">
                        <label>Скругление углов:</label>
                        <input
                          type="text"
                          placeholder="4px"
                          onChange={(e) => handleCustomStyleChange('borderRadius', e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Дополнительный CSS класс:</label>
                      <input
                        type="text"
                        placeholder="my-custom-container"
                        onChange={(e) => handleCustomSettingChange('cssClass', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="modal-footer">
          <button 
            className="btn btn-secondary" 
            onClick={onClose}
            disabled={creating}
          >
            Отмена
          </button>
          <button 
            className="btn btn-primary"
            onClick={handleCreateContainer}
            disabled={creating || !selectedTemplateId || !containerName.trim()}
          >
            {creating ? 'Создание...' : 'Создать контейнер'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContainerCreationModal;
